import { z } from 'zod';

import { getEditableTextByLanguage } from '@/utils/localization';

export const MIN_PERFORMANCE_SCORE_LEVELS = 3;
export const MAX_PERFORMANCE_SCORE_LEVELS = 6;
export const PERFORMANCE_SCORE_LEVEL_LABEL_MAX_LENGTH = 100;

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const localizedTextSchema = z.record(
    z.string(),
    z.string().max(PERFORMANCE_SCORE_LEVEL_LABEL_MAX_LENGTH),
);

const localizedDescriptionSchema = z.record(z.string(), z.string());

const createLabelSchema = (language: string) =>
    localizedTextSchema.refine(
        (label) => getEditableTextByLanguage(label, language).trim().length > 0,
        'competencies.performanceScoreLevels.validation.titleRequired',
    );

export const createPerformanceScoreLevelItemSchema = (language: string) =>
    z.object({
        id: z.string().nullable().optional(),
        icon: z.string().min(1, 'competencies.performanceScoreLevels.validation.iconRequired'),
        color: z
            .string()
            .min(1, 'competencies.performanceScoreLevels.validation.colorRequired')
            .regex(HEX_COLOR_REGEX, 'competencies.performanceScoreLevels.validation.colorInvalid'),
        label: createLabelSchema(language),
        description: localizedDescriptionSchema,
        minScore: z
            .number()
            .int('competencies.performanceScoreLevels.validation.minScoreInteger')
            .min(0, 'competencies.performanceScoreLevels.validation.minScoreRange')
            .max(100, 'competencies.performanceScoreLevels.validation.minScoreRange')
            .optional(),
        sortOrder: z.number().int().min(0),
    });

export const createPerformanceScoreLevelFormSchema = (language: string) =>
    z.object({
        levels: z
            .array(createPerformanceScoreLevelItemSchema(language))
            .min(
                MIN_PERFORMANCE_SCORE_LEVELS,
                'competencies.performanceScoreLevels.validation.levelCountMin',
            )
            .max(
                MAX_PERFORMANCE_SCORE_LEVELS,
                'competencies.performanceScoreLevels.validation.levelCountMax',
            )
            .refine((levels) => {
                const scores = levels.map((l) => l.minScore).filter((s) => s !== undefined);
                const uniqueScores = new Set(scores);
                return uniqueScores.size === scores.length;
            }, 'competencies.performanceScoreLevels.validation.thresholdsUnique')
            .refine((levels) => {
                const scores = levels.map((l) => l.minScore).filter((s) => s !== undefined);
                for (let i = 0; i < scores.length - 1; i++) {
                    if (scores[i] <= scores[i + 1]) {
                        return false;
                    }
                }
                return true;
            }, 'competencies.performanceScoreLevels.validation.thresholdsDescending')
            .refine((levels) => {
                const scores = levels.map((l) => l.minScore).filter((s) => s !== undefined);
                return scores.length > 0 && scores[scores.length - 1] === 0;
            }, 'competencies.performanceScoreLevels.validation.lowestThresholdZero'),
    });

export const performanceScoreLevelItemSchema = createPerformanceScoreLevelItemSchema('en');
export const performanceScoreLevelFormSchema = createPerformanceScoreLevelFormSchema('en');

export type PerformanceScoreLevelFormValues = z.infer<typeof performanceScoreLevelFormSchema>;
